import { motion, AnimatePresence } from "framer-motion";

interface StateData {
  name: string;
  code: string;
  responseTime: string;
  exemptions: number;
  statuteName?: string;
  statuteCitation?: string;
  population?: number;
  capitalCity?: string;
  officialWebsite?: string;
}

interface StateModalProps {
  state: StateData | null;
  onClose: () => void;
}

export function StateModal({ state, onClose }: StateModalProps) {
  if (!state) return null;

  return (
    <AnimatePresence>
      {state && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 z-40"
          />

          {/* Modal - Draggable and Throwable! */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            drag
            dragConstraints={{
              top: -window.innerHeight / 2 + 100,
              bottom: window.innerHeight / 2 - 100,
              left: -window.innerWidth / 2 + 100,
              right: window.innerWidth / 2 - 100,
            }}
            dragElastic={0.2}
            dragMomentum={true}
            whileDrag={{ scale: 1.05, cursor: "grabbing" }}
            className="fixed top-1/2 left-1/2 z-50 w-full max-w-lg cursor-grab"
            style={{ x: "-50%", y: "-50%" }}
          >
            <div className="bg-brand-dark rounded-lg p-8 shadow-2xl border border-brand-orange/30 mx-4">
              {/* Close button */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 text-brand-gray hover:text-white transition-colors"
                aria-label="Close"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>

              {/* Header */}
              <div className="mb-6">
                <h2 className="font-muller text-3xl font-bold text-white mb-2">
                  {state.name}
                </h2>
                <p className="text-brand-orange text-sm font-muller font-medium">
                  {state.code}
                </p>
              </div>

              {/* Statute Info */}
              {state.statuteName && (
                <div className="mb-6 pb-6 border-b border-brand-orange/20">
                  <p className="text-xs text-brand-orange font-muller font-medium uppercase mb-2">
                    Statute
                  </p>
                  <p className="text-white font-muller text-lg font-medium mb-1">
                    {state.statuteName}
                  </p>
                  {state.statuteCitation && (
                    <p className="text-brand-gray text-sm font-muller">
                      {state.statuteCitation}
                    </p>
                  )}
                </div>
              )}

              {/* Details Grid */}
              <div className="space-y-4 mb-6">
                <div>
                  <p className="text-xs text-brand-orange font-muller font-medium uppercase">
                    Response Time
                  </p>
                  <p className="text-white font-muller text-sm mt-1">
                    {state.responseTime}
                  </p>
                </div>

                <div>
                  <p className="text-xs text-brand-orange font-muller font-medium uppercase">
                    Common Exemptions
                  </p>
                  <p className="text-white font-muller text-sm mt-1">
                    {state.exemptions} exemptions apply
                  </p>
                </div>

                {/* Additional Info */}
                {(state.capitalCity || state.population) && (
                  <div className="pt-4 border-t border-brand-orange/10">
                    {state.capitalCity && (
                      <p className="text-brand-gray text-xs font-muller">
                        Capital: {state.capitalCity}
                      </p>
                    )}
                    {state.population && (
                      <p className="text-brand-gray text-xs font-muller mt-1">
                        Population: {state.population.toLocaleString()}
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="space-y-3 pt-4 border-t border-brand-orange/20">
                <a
                  href="/foia-generator"
                  className="block w-full px-4 py-3 bg-brand-azure text-white font-muller font-medium rounded hover:bg-brand-azure/90 transition-colors text-sm text-center"
                >
                  Generate FOIA Request
                </a>
                <a
                  href={`/wiki/${state.code.toLowerCase()}`}
                  className="block w-full px-4 py-3 bg-brand-martinique/50 text-white font-muller font-medium rounded hover:bg-brand-martinique/70 transition-colors text-sm text-center"
                >
                  View in Wiki
                </a>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
